import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Ban,
  FileText,
  Hash,
  Power,
  RefreshCw,
  RotateCcw,
  Settings2,
  TrendingUp,
  X,
} from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Field,
  Input,
  Spinner,
} from '@pnl/ui';
import { PnlApiError } from '@pnl/sdk';
import type { AdminAccountDetail, AdminAccountItem } from '@pnl/shared';
import { api } from '../lib/api';

function fmtBrl(cents: number, signed = false): string {
  const v = cents / 100;
  const s = v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  return signed && cents > 0 ? `+${s}` : s;
}

const statusTone: Record<
  AdminAccountItem['status'],
  'neutral' | 'brand' | 'success' | 'warning' | 'danger'
> = {
  TEST: 'warning',
  FUNDED: 'success',
  LIVE: 'brand',
  BLOWN: 'danger',
  RESTARTED: 'neutral',
  CLOSED: 'neutral',
};

interface Props {
  /** 'funded' lista FUNDED+LIVE; 'testing' lista TEST. */
  variant: 'funded' | 'testing';
  /** Filtra as contas por mercado; o título do card vira "B3"/"Forex". */
  marketCode?: 'B3' | 'FOREX';
}

export function AccountsManager({ variant, marketCode }: Props) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // 'funded' busca FUNDED (e a tela tb mostra LIVE via 2ª query); simplificamos
  // filtrando por phase. Pra funded mostramos FUNDED e LIVE juntos.
  const phase = variant === 'testing' ? 'TEST' : 'FUNDED';
  const marketLabel = marketCode === 'FOREX' ? 'Forex' : marketCode === 'B3' ? 'B3' : null;

  const query = useQuery({
    queryKey: ['admin', 'accounts', variant, marketCode ?? 'ALL', q],
    queryFn: () =>
      api.listAdminAccounts({
        phase,
        q: q || undefined,
        pageSize: 100,
        ...(marketCode ? { marketCode } : {}),
      }),
  });

  // Live entra junto na aba funded
  const liveQuery = useQuery({
    queryKey: ['admin', 'accounts', 'live', marketCode ?? 'ALL', q],
    queryFn: () =>
      api.listAdminAccounts({
        phase: 'LIVE',
        q: q || undefined,
        pageSize: 100,
        ...(marketCode ? { marketCode } : {}),
      }),
    enabled: variant === 'funded',
  });

  const items = [...(query.data?.items ?? []), ...(liveQuery.data?.items ?? [])];

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex w-full flex-wrap items-center justify-between gap-3">
            {marketLabel ? (
              <div>
                <CardTitle>{marketLabel}</CardTitle>
                <CardDescription>
                  {items.length} conta(s){query.isLoading ? '…' : ''}
                </CardDescription>
              </div>
            ) : (
              <CardTitle>
                {items.length} conta(s){query.isLoading ? '…' : ''}
              </CardTitle>
            )}
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por trader, email ou ID"
              className="max-w-xs"
            />
          </div>
        </CardHeader>
        <CardBody>
          {query.isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<TrendingUp size={28} />}
              title="Nenhuma conta nesta categoria"
              description="Contas aparecem aqui conforme os traders são ativados e operam."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-ink-100 text-2xs text-ink-500 border-b text-left font-semibold uppercase tracking-widest">
                    <th className="px-3 py-2.5">Trader / Conta</th>
                    {!marketCode && <th className="px-2 py-2.5">Mercado</th>}
                    <th className="px-2 py-2.5 text-right">Capital</th>
                    <th className="px-2 py-2.5 text-right">Saldo</th>
                    <th className="px-2 py-2.5 text-right">Drawdown</th>
                    <th className="px-2 py-2.5 text-right">Meta</th>
                    <th className="px-2 py-2.5 text-right">Dias</th>
                    <th className="px-3 py-2.5">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((a) => (
                    <tr
                      key={a.id}
                      onClick={() => setSelectedId(a.id)}
                      className="border-ink-100 hover:bg-ink-50/60 cursor-pointer border-b"
                    >
                      <td className="px-3 py-2.5">
                        <p className="text-ink-900 font-medium">{a.traderName ?? a.traderEmail}</p>
                        <p className="text-ink-500 text-xs">
                          {a.label} · {a.traderEmail}
                        </p>
                      </td>
                      {!marketCode && <td className="text-ink-600 px-2 py-2.5">{a.marketCode}</td>}
                      <td className="text-ink-700 px-2 py-2.5 text-right tabular-nums">
                        {fmtBrl(a.capitalCents)}
                      </td>
                      <td className="text-ink-900 px-2 py-2.5 text-right font-semibold tabular-nums">
                        {fmtBrl(a.currentBalanceCents)}
                      </td>
                      <td className="text-warning-700 px-2 py-2.5 text-right tabular-nums">
                        {a.drawdownUsedCents > 0 ? fmtBrl(a.drawdownUsedCents) : '—'}
                      </td>
                      <td className="text-ink-700 px-2 py-2.5 text-right tabular-nums">
                        {a.metaProgressPct.toFixed(0)}%
                      </td>
                      <td className="text-ink-700 px-2 py-2.5 text-right tabular-nums">
                        {a.tradingDays}
                      </td>
                      <td className="px-3 py-2.5">
                        <Badge tone={statusTone[a.status]}>{a.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {selectedId && (
        <AccountDrawer
          accountId={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            void qc.invalidateQueries({ queryKey: ['admin', 'accounts'] });
          }}
        />
      )}
    </>
  );
}

function AccountDrawer({
  accountId,
  onClose,
  onChanged,
}: {
  accountId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const qc = useQueryClient();
  const detailQuery = useQuery({
    queryKey: ['admin', 'account', accountId],
    queryFn: () => api.getAdminAccount(accountId),
  });

  const [mode, setMode] = useState<
    'view' | 'adjust' | 'reset' | 'notApproved' | 'note' | 'editNumber'
  >('view');
  const [zeroPoint, setZeroPoint] = useState('');
  const [balance, setBalance] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [nelogicaId, setNelogicaId] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  function refresh() {
    void qc.invalidateQueries({ queryKey: ['admin', 'account', accountId] });
    onChanged();
  }

  const recalc = useMutation({
    mutationFn: () => api.recalculateAccount(accountId),
    onSuccess: refresh,
    onError: (e) =>
      setActionError(e instanceof PnlApiError ? e.api.message : 'Falha ao recalcular'),
  });
  const promote = useMutation({
    mutationFn: () => api.promoteAccountLive(accountId),
    onSuccess: refresh,
    onError: (e) => setActionError(e instanceof PnlApiError ? e.api.message : 'Falha ao promover'),
  });
  const adjust = useMutation({
    mutationFn: () =>
      api.adjustAccount(accountId, {
        reason,
        ...(zeroPoint ? { zeroPointCents: Math.round(parseFloat(zeroPoint) * 100) } : {}),
        ...(balance ? { currentBalanceCents: Math.round(parseFloat(balance) * 100) } : {}),
      }),
    onSuccess: () => {
      setMode('view');
      setZeroPoint('');
      setBalance('');
      setReason('');
      refresh();
    },
    onError: (e) => setActionError(e instanceof PnlApiError ? e.api.message : 'Falha ao ajustar'),
  });
  const reset = useMutation({
    mutationFn: () => api.resetAccount(accountId, { reason }),
    onSuccess: () => {
      setMode('view');
      setReason('');
      refresh();
    },
    onError: (e) => setActionError(e instanceof PnlApiError ? e.api.message : 'Falha ao resetar'),
  });
  const markNA = useMutation({
    mutationFn: () =>
      api.markAccountNotApproved(accountId, note.trim() ? { note: note.trim() } : {}),
    onSuccess: () => {
      setMode('view');
      setNote('');
      refresh();
    },
    onError: (e) =>
      setActionError(e instanceof PnlApiError ? e.api.message : 'Falha ao marcar não aprovado'),
  });
  const restartToggle = useMutation({
    mutationFn: (enabled: boolean) => api.setAccountRestartOffer(accountId, { enabled }),
    onSuccess: refresh,
    onError: (e) =>
      setActionError(e instanceof PnlApiError ? e.api.message : 'Falha ao alterar a oferta'),
  });
  const restartAvailableToggle = useMutation({
    mutationFn: (enabled: boolean) => api.setAccountRestartAvailable(accountId, { enabled }),
    onSuccess: refresh,
    onError: (e) =>
      setActionError(e instanceof PnlApiError ? e.api.message : 'Falha ao liberar o restart'),
  });
  const saveNote = useMutation({
    mutationFn: () => api.setAccountClosureNote(accountId, { note: note.trim() || null }),
    onSuccess: () => {
      setMode('view');
      setNote('');
      refresh();
    },
    onError: (e) =>
      setActionError(e instanceof PnlApiError ? e.api.message : 'Falha ao salvar motivo'),
  });
  const setNumber = useMutation({
    mutationFn: () =>
      api.setAccountNelogicaId(accountId, { subAccountNelogicaId: nelogicaId.trim() }),
    onSuccess: () => {
      setMode('view');
      setNelogicaId('');
      refresh();
    },
    onError: (e) =>
      setActionError(e instanceof PnlApiError ? e.api.message : 'Falha ao editar o número'),
  });

  const d: AdminAccountDetail | undefined = detailQuery.data;

  return (
    <div
      className="bg-ink-900/40 fixed inset-0 z-40 flex justify-end"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="border-ink-200 shadow-lift w-full max-w-lg overflow-y-auto border-l bg-white p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Detalhe da conta"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">
              Conta virtual
            </p>
            <h2 className="text-ink-900 mt-1 text-xl font-semibold">{d?.label ?? '…'}</h2>
            {d && (
              <p className="text-ink-500 text-xs">
                {d.traderName ?? d.traderEmail} · #{d.subAccountNelogicaId}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-500 hover:bg-ink-100 rounded p-1.5"
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </div>

        {detailQuery.isLoading || !d ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : (
          <div className="mt-5 space-y-5">
            {/* KPIs */}
            <div className="grid grid-cols-2 gap-3">
              <KpiCell label="Capital (ponto zero)" value={fmtBrl(d.zeroPointCents)} />
              <KpiCell label="Saldo atual" value={fmtBrl(d.currentBalanceCents)} />
              <KpiCell
                label="Drawdown usado"
                value={`${fmtBrl(d.drawdownUsedCents)} / ${fmtBrl(d.drawdownLimitCents)}`}
              />
              <KpiCell label="Meta" value={`${d.metaProgressPct.toFixed(1)}%`} />
              <KpiCell label="Dias operados" value={String(d.tradingDays)} />
              <KpiCell label="Status / Fase" value={`${d.status} · ${d.phase}`} />
            </div>

            {actionError && <Alert tone="danger">{actionError}</Alert>}

            {/* Ações */}
            {mode === 'view' && (
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  leadingIcon={<RefreshCw size={13} />}
                  loading={recalc.isPending}
                  onClick={() => {
                    setActionError(null);
                    recalc.mutate();
                  }}
                >
                  Recalcular saldo
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  leadingIcon={<Settings2 size={13} />}
                  onClick={() => {
                    setActionError(null);
                    setMode('adjust');
                  }}
                >
                  Ajustar valores
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  leadingIcon={<Hash size={13} />}
                  onClick={() => {
                    setActionError(null);
                    setNelogicaId(d.subAccountNelogicaId);
                    setMode('editNumber');
                  }}
                >
                  Editar nº da conta
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  leadingIcon={<RotateCcw size={13} />}
                  onClick={() => {
                    setActionError(null);
                    setMode('reset');
                  }}
                >
                  Resetar conta
                </Button>
                {d.phase === 'FUNDED' && (
                  <Button
                    size="sm"
                    leadingIcon={<TrendingUp size={13} />}
                    loading={promote.isPending}
                    onClick={() => {
                      setActionError(null);
                      promote.mutate();
                    }}
                  >
                    Promover a Live
                  </Button>
                )}
                {d.status !== 'BLOWN' && (
                  <Button
                    size="sm"
                    variant="danger"
                    leadingIcon={<Ban size={13} />}
                    onClick={() => {
                      setActionError(null);
                      setNote('');
                      setMode('notApproved');
                    }}
                  >
                    Marcar não aprovado
                  </Button>
                )}
                {d.status === 'BLOWN' && (
                  <>
                    <Button
                      size="sm"
                      variant="secondary"
                      leadingIcon={<Power size={13} />}
                      loading={restartToggle.isPending}
                      onClick={() => {
                        setActionError(null);
                        restartToggle.mutate(!d.restartOfferEnabled);
                      }}
                    >
                      {d.restartOfferEnabled
                        ? 'Desligar oferta de restart'
                        : 'Ligar oferta de restart'}
                    </Button>
                    <Button
                      size="sm"
                      variant={d.restartAvailable ? 'secondary' : 'primary'}
                      leadingIcon={<RotateCcw size={13} />}
                      loading={restartAvailableToggle.isPending}
                      onClick={() => {
                        setActionError(null);
                        restartAvailableToggle.mutate(!d.restartAvailable);
                      }}
                    >
                      {d.restartAvailable ? 'Revogar restart incluso' : 'Liberar restart incluso'}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      leadingIcon={<FileText size={13} />}
                      onClick={() => {
                        setActionError(null);
                        setNote(d.closureNote ?? '');
                        setMode('note');
                      }}
                    >
                      Editar motivo
                    </Button>
                  </>
                )}
              </div>
            )}

            {mode === 'adjust' && (
              <div className="border-ink-200 space-y-3 rounded-lg border p-4">
                <p className="text-ink-900 text-sm font-semibold">Ajustar valores da conta</p>
                <p className="text-ink-500 text-xs">
                  Corrija manualmente o ponto zero e/ou o saldo atual (ex.: CSV importado errado).
                  Preencha só o que quiser alterar. Fica registrado no audit log.
                </p>
                <Field label="Novo ponto zero / capital (R$)" htmlFor="zp">
                  <Input
                    id="zp"
                    value={zeroPoint}
                    onChange={(e) => setZeroPoint(e.target.value)}
                    placeholder={(d.zeroPointCents / 100).toFixed(2)}
                  />
                </Field>
                <Field label="Novo saldo atual (R$)" htmlFor="bal">
                  <Input
                    id="bal"
                    value={balance}
                    onChange={(e) => setBalance(e.target.value)}
                    placeholder={(d.currentBalanceCents / 100).toFixed(2)}
                  />
                </Field>
                <Field label="Motivo" htmlFor="reason" required>
                  <Input id="reason" value={reason} onChange={(e) => setReason(e.target.value)} />
                </Field>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    loading={adjust.isPending}
                    disabled={(!zeroPoint && !balance) || !reason.trim()}
                    onClick={() => adjust.mutate()}
                  >
                    Salvar
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setMode('view')}>
                    Cancelar
                  </Button>
                </div>
              </div>
            )}

            {mode === 'reset' && (
              <div className="border-danger-200 bg-danger-50/40 space-y-3 rounded-lg border p-4">
                <p className="text-ink-900 text-sm font-semibold">Resetar conta</p>
                <p className="text-ink-600 text-xs">
                  Encerra a conta atual (RESTARTED) e cria uma nova em Teste com o saldo atual como
                  novo ponto zero. O trader é notificado.
                </p>
                <Field label="Motivo" htmlFor="rreason" required>
                  <Input id="rreason" value={reason} onChange={(e) => setReason(e.target.value)} />
                </Field>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="danger"
                    loading={reset.isPending}
                    onClick={() => reset.mutate()}
                  >
                    Confirmar reset
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setMode('view')}>
                    Cancelar
                  </Button>
                </div>
              </div>
            )}

            {mode === 'notApproved' && (
              <div className="border-danger-200 bg-danger-50/40 space-y-3 rounded-lg border p-4">
                <p className="text-ink-900 text-sm font-semibold">Marcar como Não aprovado</p>
                <p className="text-ink-600 text-xs">
                  Encerra a conta (status BLOWN), habilita a oferta de restart e notifica o trader.
                  O motivo abaixo aparece no aviso do dashboard (opcional).
                </p>
                <Field label="Motivo (opcional)" htmlFor="na-note">
                  <Input
                    id="na-note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Ex.: Drawdown atingido em 28/05 — perda de R$ 2.676,14 no dia."
                  />
                </Field>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="danger"
                    loading={markNA.isPending}
                    onClick={() => markNA.mutate()}
                  >
                    Confirmar não aprovado
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setMode('view')}>
                    Cancelar
                  </Button>
                </div>
              </div>
            )}

            {mode === 'note' && (
              <div className="border-ink-200 space-y-3 rounded-lg border p-4">
                <p className="text-ink-900 text-sm font-semibold">Editar motivo de eliminação</p>
                <p className="text-ink-600 text-xs">
                  Sobrepõe o motivo derivado automaticamente. Vazio = volta ao texto automático.
                </p>
                <Field label="Mensagem de motivo" htmlFor="closure-note">
                  <Input id="closure-note" value={note} onChange={(e) => setNote(e.target.value)} />
                </Field>
                <div className="flex gap-2">
                  <Button size="sm" loading={saveNote.isPending} onClick={() => saveNote.mutate()}>
                    Salvar motivo
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setMode('view')}>
                    Cancelar
                  </Button>
                </div>
              </div>
            )}

            {mode === 'editNumber' && (
              <div className="border-ink-200 space-y-3 rounded-lg border p-4">
                <p className="text-ink-900 text-sm font-semibold">Editar número da conta</p>
                <p className="text-ink-600 text-xs">
                  Atualize o ID da subconta na Nelogica — útil quando ele muda após um restart. Fica
                  registrado no audit log.
                </p>
                <Field label="Número / ID da subconta (Nelogica)" htmlFor="nelogica-id" required>
                  <Input
                    id="nelogica-id"
                    value={nelogicaId}
                    onChange={(e) => setNelogicaId(e.target.value)}
                  />
                </Field>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    loading={setNumber.isPending}
                    disabled={!nelogicaId.trim() || nelogicaId.trim() === d.subAccountNelogicaId}
                    onClick={() => setNumber.mutate()}
                  >
                    Salvar número
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setMode('view')}>
                    Cancelar
                  </Button>
                </div>
              </div>
            )}

            {/* Operações recentes */}
            <div>
              <p className="text-ink-900 mb-2 text-sm font-semibold">Operações recentes</p>
              {d.recentTrades.length === 0 ? (
                <p className="text-ink-500 text-xs">Nenhuma operação importada ainda.</p>
              ) : (
                <div className="border-ink-100 max-h-56 overflow-y-auto rounded-lg border">
                  <table className="w-full text-xs">
                    <tbody>
                      {d.recentTrades.map((t) => (
                        <tr key={t.id} className="border-ink-100 border-b last:border-b-0">
                          <td className="text-ink-500 px-3 py-1.5">
                            {new Date(t.tradeDatetime).toLocaleString('pt-BR')}
                          </td>
                          <td className="text-ink-900 px-2 py-1.5 font-medium">{t.symbol}</td>
                          <td className="px-2 py-1.5">{t.side}</td>
                          <td
                            className={
                              'px-3 py-1.5 text-right font-semibold tabular-nums ' +
                              (t.pnlCents >= 0 ? 'text-success-700' : 'text-danger-700')
                            }
                          >
                            {fmtBrl(t.pnlCents, true)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-ink-200 rounded-lg border bg-white px-3 py-2">
      <p className="text-2xs text-ink-500 font-semibold uppercase tracking-widest">{label}</p>
      <p className="text-ink-900 mt-0.5 text-sm font-semibold tabular-nums">{value}</p>
    </div>
  );
}
